<template>
  <div>
    <div class="lv-meta-links fds">
      <p class="desc mb-0">
        <icon glyph="related-object" class="mr-2" aria-hidden="true"></icon>
        {{hasEntryRelation ? "1" : "0"}} record{{hasEntryRelation ? "" : "s"}} (limit 1)
      </p>
      <p v-if="shouldShowSearchDropdown" class="desc">
        <button
          id="selection-title"
          class="btn-link"
          data-toggle="collapse"
          :data-target="`#selections-${relationSectionRelation.client_id}`"
          aria-expanded="false"
          aria-controls="selections">
          {{ searchText }}
          <icon glyph="arrow-chevron" tag="small" aria-hidden="true"></icon>
        </button>
      </p>
      <p v-if="shouldShowDisconnect" class="desc">
        <button
          class="btn-link"
          @click="showDisconnectModal">
          Disconnect Record
        </button>
      </p>
      <p class="desc mb-0" v-if="shouldShowLinkToView">
        <a :href="detailViewLink" class="btn-link">View Record<icon glyph="open-file" class="ml-2" tag="small" aria-hidden="true"></icon></a>
      </p>
    </div>

    <div :id="`selections-${relationSectionRelation.client_id}`" class="collapse" :aria-labelledby="`selection-title-${relationSectionRelation.client_id}`">
      <a-select
        v-if="shouldShowObjectModelSelect"
        :id="`object-model-select-${relationSectionRelation.client_id}`"
        :options="objectModelOptions"
        @input="updateObjectModelId"
        placeholder="Choose object model"
      />

      <a-select
        :id="relationSectionRelation.client_id"
        compress
        :options="entryOptions(relationSectionRelation)"
        @input="entryId => onSelectEntry(relationSectionRelation, entryId)"
        @search:finished="addEntriesToRepo"
        :disabled="shouldDisableEntrySearch"
      />
    </div>

    <layout-element-grid
      v-if="shouldShowRelationSection"
      :content-element="contentElement"
      :entry="entry"
      :is-in-edit-mode="isInEditMode"
      :key="contentElement.client_id"
    ></layout-element-grid>

    <confirmation-modal
      v-if="modalStateFor(vueModalID.relationSectionDisconnect).isVisible"
      :state="modalStateFor(vueModalID.relationSectionDisconnect)"
      :index="vueModalID.relationSectionDisconnect"
      @toggle-modal="hideModal(vueModalID.relationSectionDisconnect)"
      @affirm-cta="disconnectRecord"
      :override-config="disconnectModelConfig"
    ></confirmation-modal>
  </div>
</template>

<script lang="ts">
  import Vue from "vue";
  import ObjectModelEntry from "~/models/objectModelEntries/objectModelEntry";
  import { RouteHelper } from "~/helpers/routeHelper";
  import { Route } from "~/enums/route";
  import ObjectModelRelation from "~/models/objectModelRelations/objectModelRelation";
  import ContentElementComponent from "./content-element-component.vue";
  import breadcrumbMixin from "../mixins/breadcrumbMixin";
  import LayoutElementGrid from "./layout-element-grid.vue"
  import {IVOptionSetEndpoint, IVOptionSetLocal} from "~/interfaces/vSelectInterfaces";
  import {IObjectModelEntriesResponse, IServerData} from "~/interfaces/serverInterfaces";
  import ObjectModel from "~/models/objectModel";
  import saveObjectModelEntryMixin from "~/mixins/objectModelEntries/saveObjectModelEntryMixin";
  import ConfirmationModal from "~/components/modals/confirmationModal.vue";
  import RefreshEntryDataBus from '../buses/refreshEntryDataBus';
  import contextMixin from "../mixins/contextMixin";

  export default Vue.extend({
    extends: ContentElementComponent,
    name: "detail-view-content-element-relation-section",
    mixins: [
      breadcrumbMixin,
      saveObjectModelEntryMixin,
      contextMixin,
    ],
    components: {
      LayoutElementGrid,
      ConfirmationModal,
    },
    props: {
      objectModelEntry: {
        type: ObjectModelEntry
      }
    },
    data: function() {
      return {
        entries: null,
        relationSectionRelation: null,
        isDisconnectingRelation: false,
        searchObjectModelId: null,
        isInverse: null,
        relatedEntry: null,
      };
    },
    beforeMount() {
      this.loadData();
    },
    mounted() {
      this.registerEventBusListeners();
      this.fetchRelatedEntry();
    },
    computed: {
      relationTitle(): string {
        if (this.isInverse) {
          return this.relationSectionRelation.label_title;
        } else {
          return this.relationSectionRelation.inverse_label_title;
        }
      },
      relationIsRequired(): boolean {
        return this.relationSectionRelation.is_required;
      },
      relationIsPolymorphic(): boolean {
        return this.relationSectionRelation.related_object_model_ids.length > 1;
      },
      shouldShowObjectModelSelect(): boolean {
        return this.relationIsPolymorphic && !this.isInverse;
      },
      shouldShowSearchDropdown(): boolean {
        if (!this.canEdit()) return false;
        if (!this.contentElement.edit_relation_selected) return false;
        const hasEntryAndRelationIsRequired = this.hasEntryRelation && this.relationIsRequired;
        return !this.hasEntryRelation || hasEntryAndRelationIsRequired;
      },
      searchText(): string {
        if (!this.hasEntryRelation) {
          return "Search to Add"
        } else if (this.hasEntryRelation && this.relationIsRequired) {
          return "Replace";
        }
      },
      shouldShowDisconnect(): boolean {
        if (!this.canEdit()) return false;
        if (!this.contentElement.edit_relation_selected) return false;
        return this.hasEntryRelation && !this.relationSectionRelation.is_required;
      },
      hasEntryRelation(): boolean {
        const hasEntryRelation = !!this.relatedEntry;
        return hasEntryRelation;
      },
      shouldShowRelationSection(): boolean {
        if (this.hasEntryRelation) {
           return true;
        } else {
          return this.contentElement.is_visible_without_record;
        }
      },
      shouldShowLinkToView(): boolean {
        const linkToViewSelected = this.contentElement.link_to_view_selected;
        const hasRelatedEntry = !!this.relatedEntry;
        return linkToViewSelected && hasRelatedEntry;
      },
      detailViewLink(): string {
        if (!this.relatedEntry) return null;
        const linkedDetailView = this.contentElement.relationSectionLinkedDetailViewsArray.find(dv => {
          return dv.related_object_model_id === this.relatedEntry.object_model_id;
        });
        const detailViewId = linkedDetailView.detail_view_id;

        let query: object;
        if (this.currentBreadcrumbs) {
          let linkedBreadcrumb: object;
          linkedBreadcrumb = {t: 'dv', v: {dv: detailViewId, e: this.relatedEntry.id}};
          query = {bc: [...this.currentBreadcrumbs, linkedBreadcrumb]};
        } else {
          query = {from: 'Reporting & Submissions Dashboard'};
        }

        // this may need to be updated to support preview
        const routeEnum = Route.detailView;
        const detailViewPath = RouteHelper.path({
          route: routeEnum,
          namedWildcardOptions: [
            {wildcard: ':id', modelOrId: detailViewId},
            {wildcard: ':entry_id', modelOrId: this.relatedEntry.id},
          ],
          query
        })
        return detailViewPath;
      },
      disconnectModelConfig(): object {
        return {
          confirmationHeader: "Disconnect from Relationship",
          confirmationMessage: "By selecting Disconnect, you will sever the relationship. You will then be able to associate to another record.",
          showAffirm: true,
          affirmText: "Disconnect",
          affirmButtonClass: "red",
          showCancel: true,
        }
      },
      shouldDisableEntrySearch(): boolean {
        if (this.isInverse) return false;
        return this.relationIsPolymorphic && !this.searchObjectModelId;
      },
      objectModelOptions(): IVOptionSetLocal {
        const objectModelIds = this.selectedDetailView.objectModel.all_related_object_models
          .filter(om => this.relationSectionRelation.related_object_model_ids.includes(om.object_model_id))
          .map(om => om.id);
        const objectModels = ObjectModel.repo.findWhere(om => objectModelIds.includes(om.id));
        const options = objectModels.map(om => {
          return { id: om.id, title: om.title };
        })
        return {
          valueKey: "id",
          labelKey: "title",
          options
        }
      },
    },
    methods: {
      registerEventBusListeners(): void {
        RefreshEntryDataBus.onRefresh(() => {
          this.fetchRelatedEntry();
        })
      },
      loadData(): void {
        const objectModel = this.selectedDetailView.objectModel;

        // finds relation for relation section and loads it
        const relations = objectModel.all_object_model_relations_with_inverse_flag;
        let relationSectionRelation = relations.find(relation => {
          if (this.isPreview) {
            return relation.id === this.contentElement.relation_id;
          } else {
            return relation.object_model_relation_id === this.contentElement.relation_id;
          }
        })
        if (this.isPreview && !relationSectionRelation) {
          relationSectionRelation = {
            id: 'fake_id',
            client_id: 'fake_client_id',
            related_object_model_ids: [],
            parent_object_model_display_properties: [],
            related_object_model_display_properties: [],
          }
        }
        this.isInverse = relationSectionRelation.inverse;
        this.relationSectionRelation = ObjectModelRelation.repo.addFromJson( relationSectionRelation);

        ObjectModel.repo.addManyFromJson(objectModel.all_related_object_models);
        ObjectModel.repo.addManyFromJson(objectModel.all_inverse_related_object_models);
      },
      async fetchRelatedEntry(): Promise<void> {
        if (this.isPreview) {
          this.relatedEntry = this.selectedObjectModelEntry
          return
        }
        const entryId = this.selectedObjectModelEntry.id;
        const publishedRelationId = this.contentElement.published_relation_id;
        const inverse = this.isInverse;

        const res = await ObjectModelEntry.repo.provider.fetchEntryRelationsAndEntries({ entryId, publishedRelationId, inverse })

        const { entries, entry_relations } = res[0];
        this.relatedEntry = entries[0];

        entry_relations.forEach(er => {
          if (inverse) {
            this.selectedObjectModelEntry.appendInverseEntryRelation(er)
          } else {
            this.selectedObjectModelEntry.appendEntryRelation(er);
          }
        })
      },
      entryOptions(relation): IVOptionSetEndpoint {
        const relationId = relation.id;

        const filterByObjectModel = entry => {
          // if the relationship is inverse or the relationship only has a single related model (not polymorphic)
          // then we don't need to filter by object model
          if (this.isInverse) return true;
          if (!this.relationIsPolymorphic) return true;
          return entry.published_object_model_id === this.searchObjectModelId;
        }

        const endpoint = this.isInverse ? "entries" : "inverse_entries";

        return {
          valueKey: "id",
          labelKey: entry => {
            return this.getLabelNames(relation, entry);
          },
          endpoint: `/published_object_model_relations/${relationId}/${endpoint}`,
          endpointFilters: { per_page: 5 },
          rootKey: "entries",
          endpointOptionFilter: filterByObjectModel,
        };
      },
      addEntriesToRepo(response: IServerData<IObjectModelEntriesResponse>): void {
        const data = response && response[0];
        if (data && data.success) {
          ObjectModelEntry.provider.createOrUpdateManyFrom(data.entries);
        }
      },
      onSelectEntry(relation: ObjectModelRelation, newEntryId): void {
        const newEntry = ObjectModelEntry.repo.find(newEntryId);
        let entry;
        let relatedEntry;

        if (this.isInverse) {
          entry = newEntry;
          relatedEntry = this.selectedObjectModelEntry;
        } else {
          entry = this.selectedObjectModelEntry;
          relatedEntry = newEntry;
        }

        const json = {
          published_relation_id: relation.id,
          entry_id: entry.id,
          related_entry_id: relatedEntry.id
        }

        let newRelation;
        if (this.isInverse) {
          newRelation = this.selectedObjectModelEntry.appendInverseEntryRelation(json);
        } else {
          newRelation = this.selectedObjectModelEntry.appendEntryRelation(json);
        }

        this.save();
      },
      disconnectRecord(): void {
        const entry = this.selectedObjectModelEntry;
        let relation;

        if (this.isInverse) {
          for (const inverseEntryRelation of entry.inverseEntryRelationsArray) {
            if (inverseEntryRelation.entry_id === this.relatedEntry.id) {
              relation = inverseEntryRelation;
            }
          }
        } else {
          for (const entryRelation of entry.entryRelationsArray) {
            if (entryRelation.related_entry_id === this.relatedEntry.id) {
              relation = entryRelation;
            }
          }
        }

        this.$store.commit("objectModelEntriesStore/addEntryRelationForDeletion", relation);
        this.isDisconnectingRelation = true;
        this.save();
      },
      getLabelNames(relation, entry): string {
        let displayProps;
        if (this.isInverse) {
          displayProps = relation.parent_object_model_display_properties.map(prop => "f_" + prop);
        } else {
          displayProps = relation.related_object_model_display_properties.map(prop => "f_" + prop);
        }

        const propertyDisplayProps = displayProps.map(prop => {

          const omr2 = ObjectModel.repo.findWhere(om => om.id === entry.published_object_model_id)[0];
          const propObj = omr2.object_model_properties[prop.replace("f_", "")]
          if (propObj && propObj._type === "ObjectModels::Properties::DateTime") {
            return this.parseDate(entry[prop], propObj)
          }

          if (Array.isArray(entry[prop])) {
            const omr = ObjectModel.repo.findWhere(om => om.id === entry.published_object_model_id)[0];
            for (let index = 0; index < Object.keys(omr.object_model_properties).length; index++) {
              const key = Object.keys(omr.object_model_properties)[index];
              const element = omr.object_model_properties[key];
              if (element.object_model_properties[entry[prop]]) {
                return element.object_model_properties[entry[prop]].label_title;
              }
            }
          }

          return entry[prop];
        })

        const defaultDisplayProps = relation.relatedObjectModelDisplayDefaultPropertiesArray.map(x => x.defaultPropertyValueForEntry(entry));

        return [...propertyDisplayProps, ...defaultDisplayProps].join(" ");
      },
      redirectToView(flash, options) {
        const { job_ids } = options || {};
        const params = this.$route.params;
        const relation_title = this.relationTitle;
        const is_disconnecting = this.isDisconnectingRelation ? true : null;
        window.location.href = RouteHelper.path({
          route: Route.detailView,
          namedWildcardOptions: [
            {wildcard: ":id", modelOrId: params.id},
            {wildcard: ":entry_id", modelOrId: params.entry_id}
          ],
          query: { job_ids, relation_title, is_disconnecting }
        });
      },
      showDisconnectModal(): void {
        this.showModal(this.vueModalID.relationSectionDisconnect);
      },
      updateObjectModelId(objectModelId): void {
        this.searchObjectModelId = objectModelId;
      },
      canEdit(): boolean {
        const objectModel = this.selectedDetailView.objectModel;
        return this.$can('connectOrSever', this.relationSectionRelation, { objectModel });
      },
    }
  });
</script>